// Outbound calling utility functions and config caching

const OUTBOUND_CONFIG_KEY = 'outbound_call_config';
const CONFIG_EXPIRY_KEY = 'outbound_call_config_expiry';
const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours (matches server-side Redis cache)

export interface OutboundCallConfig {
  uuid: string;
  provider: 'knowlarity' | 'tata_tele' | 'mcube';
  provider_name: string;
  is_active: boolean;
}

// Pending request promise to deduplicate concurrent fetches
let pendingFetchPromise: Promise<OutboundCallConfig | null> | null = null;

/**
 * Save outbound config to localStorage with expiry
 */
export function cacheOutboundConfig(config: OutboundCallConfig | null): void {
  if (config) {
    localStorage.setItem(OUTBOUND_CONFIG_KEY, JSON.stringify(config));
    localStorage.setItem(CONFIG_EXPIRY_KEY, String(Date.now() + CACHE_DURATION));
  } else {
    localStorage.removeItem(OUTBOUND_CONFIG_KEY);
    localStorage.removeItem(CONFIG_EXPIRY_KEY);
  }
}

/**
 * Get cached outbound config from localStorage
 * Returns null if expired or not found
 */
export function getCachedOutboundConfig(): OutboundCallConfig | null {
  try {
    const expiryStr = localStorage.getItem(CONFIG_EXPIRY_KEY);
    const configStr = localStorage.getItem(OUTBOUND_CONFIG_KEY);

    if (!expiryStr || !configStr) {
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    if (Date.now() > expiry) {
      // Cache expired, clear it
      clearOutboundConfigCache();
      return null;
    }

    return JSON.parse(configStr);
  } catch (error) {
    console.error('Error reading cached config:', error);
    return null;
  }
}

/**
 * Clear cached outbound config
 */
export function clearOutboundConfigCache(): void {
  localStorage.removeItem(OUTBOUND_CONFIG_KEY);
  localStorage.removeItem(CONFIG_EXPIRY_KEY);
}

/**
 * Check if outbound calling is configured and active
 */
export function isOutboundCallingAvailable(): boolean {
  const config = getCachedOutboundConfig();
  return config !== null && config.is_active;
}

/**
 * Fetch outbound config with request deduplication
 * Multiple concurrent calls will share the same promise
 */
export async function fetchOutboundConfig(
  fetchFn: () => Promise<{ success: boolean; data?: any }>
): Promise<OutboundCallConfig | null> {
  // Check cache first
  const cached = getCachedOutboundConfig();
  if (cached) {
    console.log('OutboundConfig: Using cached config');
    return cached;
  }

  // If there's already a pending request, wait for it
  if (pendingFetchPromise) {
    console.log('OutboundConfig: Waiting for pending request');
    return pendingFetchPromise;
  }

  // Create new fetch promise
  console.log('OutboundConfig: Fetching from API');
  pendingFetchPromise = (async () => {
    try {
      const response = await fetchFn();

      if (response.success && response.data && response.data.uuid) {
        const config: OutboundCallConfig = {
          uuid: response.data.uuid,
          provider: response.data.provider,
          provider_name: response.data.provider_name,
          is_active: response.data.is_active
        };
        cacheOutboundConfig(config);
        return config;
      }
      return null;
    } catch (error) {
      console.error('Error fetching outbound config:', error);
      return null;
    } finally {
      pendingFetchPromise = null;
    }
  })();

  return pendingFetchPromise;
}
